import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Ban, CheckCircle2, FilePen, Handshake, Printer, XCircle } from 'lucide-react';
import { api } from '@/shared/api/cliente';
import { ErrorDeApi } from '@/shared/api/problema';
import { useSesion } from '@/shared/sesion/SesionProvider';
import { CargandoEnLinea } from '@/shared/ui/Cargando';
import { PanelLateral } from '@/shared/ui/PanelLateral';
import { VistaPreviaDeImpresion } from '@/shared/ui/VistaPreviaDeImpresion';
import { notificar } from '@/shared/ui/avisos';
import { Etiqueta } from './Etiqueta';
import { ExpedienteDelConvenio } from './ExpedienteDelConvenio';
import { ModalDeDatosDelDocumento } from './ModalDeDatosDelDocumento';
import { GARANTIAS, enDolares, enFecha, type Convenio } from './contrato';

type Accion = 'anular' | 'rechazar' | 'incumplir';

const ACCIONES: Record<
  Accion,
  { ruta: string; abrir: string; pregunta: string; boton: string; hecho: string }
> = {
  anular: {
    ruta: 'anulacion',
    abrir: 'Anular (registrado por error)',
    pregunta: 'Por qué se anula',
    boton: 'Anular el convenio',
    hecho: 'Convenio anulado',
  },
  rechazar: {
    ruta: 'rechazo',
    abrir: 'Rechazar',
    pregunta: 'Por qué se rechaza',
    boton: 'Rechazar el convenio',
    hecho: 'Convenio rechazado',
  },
  incumplir: {
    ruta: 'incumplimiento',
    abrir: 'Dar por incumplido',
    pregunta: 'Por qué se da por incumplido',
    boton: 'Dar por incumplido',
    hecho: 'Convenio incumplido',
  },
};

export function PanelDelConvenio({ id, alCerrar }: { id: number; alCerrar: () => void }) {
  const { puede } = useSesion();
  const clienteDeConsultas = useQueryClient();
  const [accion, setAccion] = useState<Accion | null>(null);
  const [motivo, setMotivo] = useState('');
  const [documento, setDocumento] = useState<Blob | null>(null);
  const [imprimiendo, setImprimiendo] = useState(false);
  const [completando, setCompletando] = useState(false);

  async function imprimir() {
    setImprimiendo(true);
    setDocumento(null);
    try {
      const pdf = await api.descargar(`/convenios/${id}/documento.pdf`);
      if (!pdf) {
        notificar.error('NO SE PUDO GENERAR EL CONVENIO');
        setImprimiendo(false);
        return;
      }
      setDocumento(pdf);
    } catch (causa) {
      notificar.error(
        causa instanceof ErrorDeApi ? causa.message : 'NO SE PUDO GENERAR EL CONVENIO',
      );
      setImprimiendo(false);
    }
  }

  const convenio = useQuery({
    queryKey: ['convenios', 'ficha', id],
    queryFn: () => api.get<Convenio>(`/convenios/${id}`),
  });

  const conMotivo = useMutation({
    mutationFn: (a: Accion) =>
      api.post<Convenio>(`/convenios/${id}/${ACCIONES[a].ruta}`, { motivo }),
    onSuccess: (_, a) => {
      notificar.exito('Las facturas que recogía vuelven a ser deuda', ACCIONES[a].hecho);
      setAccion(null);
      setMotivo('');
      void clienteDeConsultas.invalidateQueries({ queryKey: ['convenios'] });
    },
    onError: (causa) =>
      notificar.error(causa instanceof ErrorDeApi ? causa.message : 'NO SE PUDO HACER'),
  });

  const aprobar = useMutation({
    mutationFn: () => api.post<Convenio>(`/convenios/${id}/aprobacion`),
    onSuccess: (a) => {
      notificar.exito(a.numero ?? a.cliente, 'Convenio aprobado');
      void clienteDeConsultas.invalidateQueries({ queryKey: ['convenios'] });
    },
    onError: (causa) =>
      notificar.error(causa instanceof ErrorDeApi ? causa.message : 'NO SE PUDO APROBAR'),
  });

  const c = convenio.data;
  const aprueba = puede('convenios.aprobar');
  const porAprobar = c?.situacion === 'POR_APROBAR';
  const vivo = c?.situacion === 'AL_DIA' || c?.situacion === 'ATRASADO';
  const aprobado = c !== undefined && !porAprobar && c.situacion !== 'RECHAZADO';
  const anulable =
    c !== undefined && aprueba && !c.migrado && vivo && c.cuotas.every((q) => !q.pagada);
  const acciones: Accion[] = !aprueba
    ? []
    : porAprobar
      ? ['rechazar']
      : vivo
        ? anulable
          ? ['incumplir', 'anular']
          : ['incumplir']
        : [];

  return (
    <PanelLateral
      abierto
      alCerrar={alCerrar}
      titulo={c ? c.cliente : 'Convenio'}
      descripcion={c ? `${c.identificacion} · ${c.servicioNombre}` : 'Cargando'}
      icono={Handshake}
    >
      {!c ? (
        <CargandoEnLinea detalle="Abriendo el convenio" />
      ) : (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <Etiqueta situacion={c.situacion} />
            {c.numero && (
              <span className="importe rounded bg-marca-50 px-1.5 py-0.5 text-[10px] font-bold text-marca-700">
                {c.numero}
              </span>
            )}
            {c.migrado && (
              <span className="rounded bg-armazon-100 px-1.5 py-0.5 text-[10px] font-bold uppercase text-armazon-500">
                Del libro de cartera · Nº {c.numeroEnElLibro}
              </span>
            )}
            {c.oportunidad === 2 && (
              <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold uppercase text-amber-800">
                Segundo convenio · si se incumple, a coactiva
              </span>
            )}
            <div className="ml-auto flex gap-2">
              {puede('convenios.gestionar') &&
                c.situacion !== 'ANULADO' &&
                c.situacion !== 'RECHAZADO' && (
                  <button
                    type="button"
                    onClick={() => setCompletando(true)}
                    className={`inline-flex h-8 items-center gap-1.5 rounded-lg border px-3 text-[11px] font-bold uppercase transition-colors ${
                      c.migrado && !c.numero
                        ? 'border-amber-300 bg-amber-50 text-amber-800 hover:border-amber-400'
                        : 'border-armazon-200 bg-white text-armazon-700 hover:border-marca-400 hover:text-marca-700'
                    }`}
                  >
                    <FilePen className="size-3.5" aria-hidden />
                    {c.migrado && !c.numero ? 'Falta el número' : 'Datos del documento'}
                  </button>
                )}
              {aprobado && (
                <button
                  type="button"
                  onClick={() => void imprimir()}
                  className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-armazon-200 bg-white px-3 text-[11px] font-bold uppercase text-armazon-700 transition-colors hover:border-marca-400 hover:text-marca-700"
                >
                  <Printer className="size-3.5" aria-hidden />
                  Imprimir el convenio
                </button>
              )}
            </div>
          </div>

          {porAprobar && (
            <div className="space-y-2 rounded-xl border border-violet-200 bg-violet-50 p-4">
              <p className="text-xs font-semibold uppercase leading-relaxed text-violet-900">
                Por aprobar. Lo registró {c.creadoPor ?? 'el sistema'}. Hasta que se apruebe no
                tiene número ni documento, y sus facturas no se cobran en caja.
              </p>
              {aprueba && (
                <button
                  type="button"
                  onClick={() => aprobar.mutate()}
                  disabled={aprobar.isPending}
                  className="inline-flex h-9 items-center gap-2 rounded-lg bg-marca-600 px-4 text-xs font-bold uppercase text-white shadow-sm hover:bg-marca-700 disabled:opacity-50"
                >
                  <CheckCircle2 className="size-4" aria-hidden />
                  Aprobar el convenio
                </button>
              )}
            </div>
          )}
          {c.situacion === 'INCUMPLIDO' && (
            <p className="rounded-xl border border-red-200 bg-red-50 p-3 text-xs font-semibold uppercase leading-relaxed text-red-800">
              Incumplido{c.incumplidoEn ? ` el ${enFecha(c.incumplidoEn)}` : ''}: sus facturas
              volvieron a ser deuda.{' '}
              {c.oportunidad === 2
                ? 'Era el segundo convenio: va a coactiva.'
                : 'Puede pedir un segundo convenio; si lo incumple, va a coactiva.'}
            </p>
          )}

          <dl className="grid grid-cols-2 gap-3 rounded-xl border border-armazon-200 bg-armazon-50/60 p-4">
            {c.interes > 0 && (
              <Dato
                rotulo="Facturas + mora a la firma"
                valor={`${enDolares(c.capital)} + ${enDolares(c.interes)}`}
              />
            )}
            <Dato rotulo="Deuda convenida" valor={enDolares(c.deuda)} />
            <Dato rotulo="Entrada" valor={enDolares(c.entrada)} />
            <Dato rotulo="Pendiente" valor={enDolares(c.pendiente)} fuerte />
            <Dato
              rotulo="Vencido"
              valor={enDolares(c.vencido)}
              {...(c.vencido > 0 ? { aviso: true } : {})}
            />
            <Dato rotulo="Plazo" valor={`${c.plazo} cuotas · día ${c.diaDePago}`} />
            <Dato rotulo="Firmado" valor={c.fecha ? enFecha(c.fecha) : 'Sin fecha en el libro'} />
            {c.aprobadoPor && (
              <Dato rotulo="Aprobado por" valor={`${c.aprobadoPor} · ${enFecha(c.aprobadoEn)}`} />
            )}
            {c.migrado && (
              <Dato rotulo="Cobrado antes de migrar" valor={enDolares(c.recuperadoAntes)} />
            )}
            <Dato
              rotulo="Garantía"
              valor={GARANTIAS.find((g) => g.valor === c.garantia)?.etiqueta ?? '—'}
            />
            {c.referencia && <Dato rotulo="Respaldo" valor={c.referencia} />}
            {c.representante && <Dato rotulo="Representante legal" valor={c.representante} />}
            {c.fechaSolicitud && (
              <Dato rotulo="Oficio de solicitud" valor={enFecha(c.fechaSolicitud)} />
            )}
            {c.concepto && <Dato rotulo="Por concepto de" valor={c.concepto} />}
          </dl>

          {c.sumilla && (
            <p className="rounded-lg border border-armazon-200 p-3 text-[11px] italic leading-relaxed text-armazon-600">
              Sumilla: «{c.sumilla}»
            </p>
          )}

          {c.observacion && (
            <p className="rounded-lg border border-armazon-200 p-3 text-[11px] uppercase leading-relaxed text-armazon-600">
              {c.observacion}
            </p>
          )}

          <section>
            <h3 className="mb-2 text-[11px] font-bold uppercase tracking-wide text-armazon-600">
              Calendario de cuotas pendientes
            </h3>
            {c.cuotas.length === 0 ? (
              <p className="rounded-lg border border-armazon-200 p-3 text-center text-[11px] uppercase text-armazon-400">
                No le queda ninguna cuota
              </p>
            ) : (
              <div className="max-h-80 overflow-y-auto rounded-xl border border-armazon-200">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-armazon-50 text-[10px] uppercase text-armazon-500">
                    <tr>
                      <th className="px-3 py-2 text-left">Nº</th>
                      <th className="px-3 py-2 text-left">Vence</th>
                      <th className="px-3 py-2 text-right">Valor</th>
                      <th className="px-3 py-2 text-right">Estado</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-armazon-100">
                    {c.cuotas.map((q) => (
                      <tr key={q.numero} className={q.vencida ? 'bg-amber-50/60' : ''}>
                        <td className="importe px-3 py-1.5 text-armazon-500">{q.numero}</td>
                        <td className="importe px-3 py-1.5 text-armazon-700">
                          {enFecha(q.vencimiento)}
                        </td>
                        <td className="importe px-3 py-1.5 text-right font-semibold text-armazon-900">
                          {enDolares(q.valor)}
                        </td>
                        <td className="px-3 py-1.5 text-right text-[10px] font-bold uppercase">
                          {q.pagada ? (
                            <span className="text-marca-700">Pagada</span>
                          ) : q.vencida ? (
                            <span className="text-amber-700">Vencida</span>
                          ) : (
                            <span className="text-armazon-400">Por vencer</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {c.facturas.length > 0 && (
            <section>
              <h3 className="mb-2 text-[11px] font-bold uppercase tracking-wide text-armazon-600">
                Facturas que recoge ({c.facturas.length})
              </h3>
              <ul className="divide-y divide-armazon-100 rounded-xl border border-armazon-200">
                {c.facturas.map((f) => (
                  <li
                    key={`${f.historica ? 'h' : 'f'}${f.id}`}
                    className="flex items-center justify-between px-3 py-1.5 text-xs"
                  >
                    <span className="importe text-armazon-700">
                      {f.numero ?? 'Sin número'}{' '}
                      <span className="text-[10px] text-armazon-400">· {f.periodo}</span>
                    </span>
                    <span className="importe text-right text-armazon-600">
                      {enDolares(f.capital)}
                      {f.interes > 0 && (
                        <span className="text-amber-700"> + {enDolares(f.interes)}</span>
                      )}
                      <b className="ml-2 text-armazon-800">{enDolares(f.total)}</b>
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          <ExpedienteDelConvenio id={c.id} anulado={c.situacion === 'ANULADO' || !aprobado} />

          {accion ? (
            <div className="space-y-2 rounded-xl border border-red-200 bg-red-50 p-3">
              <label className="grid gap-1">
                <span className="text-[10px] font-semibold uppercase text-red-800">
                  {ACCIONES[accion].pregunta}
                </span>
                <input
                  value={motivo}
                  onChange={(e) => setMotivo(e.target.value)}
                  className="h-9 rounded-lg border border-red-200 bg-white px-2 text-xs uppercase"
                />
              </label>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setAccion(null)}
                  className="h-8 rounded-lg border border-armazon-200 bg-white px-3 text-[11px] font-semibold uppercase text-armazon-600"
                >
                  No
                </button>
                <button
                  type="button"
                  onClick={() => conMotivo.mutate(accion)}
                  disabled={motivo.trim() === '' || conMotivo.isPending}
                  className="h-8 rounded-lg bg-red-600 px-3 text-[11px] font-bold uppercase text-white disabled:opacity-50"
                >
                  {ACCIONES[accion].boton}
                </button>
              </div>
            </div>
          ) : (
            <div className="flex flex-wrap gap-4">
              {acciones.map((a) => {
                const Icono = a === 'anular' ? Ban : XCircle;
                return (
                  <button
                    key={a}
                    type="button"
                    onClick={() => setAccion(a)}
                    className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase text-red-700 hover:underline"
                  >
                    <Icono className="size-3.5" aria-hidden />
                    {ACCIONES[a].abrir}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
      {completando && c && (
        <ModalDeDatosDelDocumento convenio={c} alCerrar={() => setCompletando(false)} />
      )}
      <VistaPreviaDeImpresion
        abierto={imprimiendo}
        alCerrar={() => {
          setImprimiendo(false);
          setDocumento(null);
        }}
        titulo={c?.numero ? `Convenio ${c.numero}` : 'Convenio de pago'}
        subtitulo={c?.cliente}
        fichero={documento}
        cargando={documento === null}
        formato="hoja"
        textoDeCarga="Preparando el convenio…"
      />
    </PanelLateral>
  );
}

function Dato({
  rotulo,
  valor,
  fuerte = false,
  aviso = false,
}: {
  rotulo: string;
  valor: string;
  fuerte?: boolean;
  aviso?: boolean;
}) {
  return (
    <div>
      <dt className="text-[10px] font-semibold uppercase text-armazon-400">{rotulo}</dt>
      <dd
        className={`importe text-sm uppercase ${fuerte ? 'font-bold text-armazon-900' : 'text-armazon-700'} ${
          aviso ? 'font-bold text-amber-700' : ''
        }`}
      >
        {valor}
      </dd>
    </div>
  );
}
